import { useState, useMemo, useEffect, useRef } from 'react'
import { buildStarterSlots as buildSlots } from '../../lib/rosterSlots'
import {
  useAvailablePlayers, useFantasyRoster, useAddDropPlayer,
  useFantasySettings, useWaiverState, useMyWaiverClaims, useSubmitWaiverClaim, useCancelWaiverClaim,
  useReorderWaiverClaims,
  useBlurbPlayerIds,
} from '../../hooks/useLeagues'
import { toast } from '../ui/Toast'
import PlayerDetailModal from './PlayerDetailModal'
import BlurbDot, { markBlurbSeen } from './BlurbDot'
import FantasyMyRankings from './FantasyMyRankings'
import PlayerHeadshot from '../ui/PlayerHeadshot'
import InjuryBadge from '../ui/InjuryBadge'

const OFFENSE_POSITION_FILTERS = ['All', 'QB', 'RB', 'WR', 'TE', 'K']
// No 'S' tab: Sleeper classifies essentially every defensive back as 'DB',
// so an S filter showed the six S/FS/SS rows that exist — all retired — and
// after those were filtered out as junk, nothing at all. DB carries corners
// and safeties together, which is the finest distinction the data supports.
// Leagues created before this still have an s slot; the server accepts DB
// for it, so their rosters remain fillable.
const IDP_POSITION_FILTERS = ['DL', 'LB', 'DB']

function buildPositionFilters(rosterSlots) {
  const idpCount = (rosterSlots?.dl || 0) + (rosterSlots?.lb || 0) + (rosterSlots?.db || 0) + (rosterSlots?.s || 0)
  const isIdp = idpCount > 0
  const filters = [...OFFENSE_POSITION_FILTERS]
  if (isIdp) {
    // IDP leagues: DEF is out, DL/LB/DB/S are in.
    filters.push(...IDP_POSITION_FILTERS)
  } else {
    // Team-DEF leagues: DEF is included.
    filters.push('DEF')
  }
  return filters
}


// Sortable stat columns shown in the strip on the right of each row.
// `key` matches the server-side sort param + the row.stats[key] field.
const OFFENSE_STAT_COLUMNS = [
  { key: 'weekly_proj', label: 'PROJ' },
  { key: 'pts', label: 'PTS' },
  { key: 'pass_yd', label: 'PA YD' },
  { key: 'pass_td', label: 'PA TD' },
  { key: 'pass_int', label: 'INT' },
  { key: 'rush_att', label: 'CAR' },
  { key: 'rush_yd', label: 'RU YD' },
  { key: 'rush_td', label: 'RU TD' },
  { key: 'rec_tgt', label: 'TGT' },
  { key: 'rec', label: 'REC' },
  { key: 'rec_yd', label: 'RE YD' },
  { key: 'rec_td', label: 'RE TD' },
  { key: 'fum_lost', label: 'FUM' },
  { key: 'fgm', label: 'FGM' },
  { key: 'xpm', label: 'XPM' },
  // Snap share sits LAST in every set, so it is reached by scrolling
  // rather than competing with the yards and catches people came for.
  // Season to date, like every other column here.
  { key: 'snap_pct', label: 'SNP%' },
]

const DEF_STAT_COLUMNS = [
  { key: 'weekly_proj', label: 'PROJ' },
  { key: 'pts', label: 'PTS' },
  { key: 'def_sack', label: 'SK' },
  { key: 'def_int', label: 'INT' },
  { key: 'def_fum_rec', label: 'FR' },
  { key: 'def_td', label: 'TD' },
  { key: 'def_safety', label: 'SAF' },
  { key: 'def_pts_allowed', label: 'PA' },
  // Snap share sits LAST in every set, so it is reached by scrolling
  // rather than competing with the yards and catches people came for.
  // Season to date, like every other column here.
  { key: 'snap_pct', label: 'SNP%' },
]

const IDP_STAT_COLUMNS = [
  { key: 'weekly_proj', label: 'PROJ' },
  { key: 'pts', label: 'PTS' },
  { key: 'idp_tkl_solo', label: 'SOLO' },
  { key: 'idp_tkl_ast', label: 'AST' },
  { key: 'idp_tkl_loss', label: 'TFL' },
  { key: 'idp_sack', label: 'SK' },
  { key: 'idp_int', label: 'INT' },
  { key: 'idp_pass_def', label: 'PD' },
  { key: 'idp_ff', label: 'FF' },
  { key: 'idp_fum_rec', label: 'FR' },
  // Snap share sits LAST in every set, so it is reached by scrolling
  // rather than competing with the yards and catches people came for.
  // Season to date, like every other column here.
  { key: 'snap_pct', label: 'SNP%' },
]

// InjuryBadge now imported from ui/InjuryBadge — the local copy
// missed lowercase Sleeper variants + PUP/SUS/Doubtful.

export default function FantasyPlayerBrowser({ league }) {
  const [playerView, setPlayerView] = useState('ADP') // 'ADP' | 'My Ranks'
  const [searchQuery, setSearchQuery] = useState('')
  const [posFilter, setPosFilter] = useState('All')
  const [sortKey, setSortKey] = useState('rank')
  const [sortDir, setSortDir] = useState('desc') // 'desc' = highest first, 'asc' = lowest first
  const sortInitialized = useRef(false)
  const isIdpFilter = posFilter === 'DL' || posFilter === 'LB' || posFilter === 'DB' || posFilter === 'S'
  const statColumns = posFilter === 'DEF' ? DEF_STAT_COLUMNS
    : isIdpFilter ? IDP_STAT_COLUMNS
    : OFFENSE_STAT_COLUMNS

  function handleSort(key) {
    sortInitialized.current = true
    if (key === sortKey && key !== 'rank') {
      setSortDir((d) => d === 'desc' ? 'asc' : 'desc')
    } else {
      setSortKey(key)
      setSortDir(key === 'rank' ? 'asc' : 'desc')
    }
  }
  const [addingPlayer, setAddingPlayer] = useState(null) // player being added
  const [dropPlayerId, setDropPlayerId] = useState('') // chosen drop
  const [bidAmount, setBidAmount] = useState(0)
  const [detailPlayerId, setDetailPlayerId] = useState(null)
  const { data: blurbIdsList } = useBlurbPlayerIds(league.id)
  const blurbIds = useMemo(
    () => new Map((blurbIdsList || []).map((r) => [r.player_id, r.latest_id])),
    [blurbIdsList],
  )

  function openPlayerDetail(id) {
    if (id) markBlurbSeen(id, blurbIds.get(id))
    setDetailPlayerId(id)
  }

  const { data: rawPlayers, isLoading } = useAvailablePlayers(
    league.id,
    searchQuery || undefined,
    posFilter !== 'All' ? posFilter : undefined
  )
  // Sort client-side for instant re-sort without API round-trip.
  // Server returns up to 300 players; we display 60 after sorting.
  const players = useMemo(() => {
    if (!rawPlayers) return null
    let sorted = rawPlayers
    if (sortKey && sortKey !== 'rank') {
      sorted = [...rawPlayers].sort((a, b) => {
        const av = sortKey === 'weekly_proj' ? (a.weekly_projection ?? 0) : (a.stats?.[sortKey] || 0)
        const bv = sortKey === 'weekly_proj' ? (b.weekly_projection ?? 0) : (b.stats?.[sortKey] || 0)
        return sortDir === 'desc' ? bv - av : av - bv
      })
    }
    return sorted.slice(0, 300)
  }, [rawPlayers, sortKey, sortDir])
  const { data: roster } = useFantasyRoster(league.id)
  const { data: settings } = useFantasySettings(league.id)

  // Post-draft, mirror Yahoo's default: list ranked by current-week
  // projection (highest first). Pre-draft keeps the server-provided
  // draft rank (#) since projections mean less for draft-order purposes.
  // Runs once when settings first loads; user's manual sort is
  // preserved via sortInitialized.
  useEffect(() => {
    if (sortInitialized.current) return
    if (!settings) return
    sortInitialized.current = true
    if (settings.draft_status === 'completed') {
      setSortKey('weekly_proj')
      setSortDir('desc')
    }
  }, [settings])

  const { data: waiverData } = useWaiverState(league.id)
  const { data: myClaims } = useMyWaiverClaims(league.id)
  const addDrop = useAddDropPlayer(league.id)
  const submitClaim = useSubmitWaiverClaim(league.id)
  const cancelClaim = useCancelWaiverClaim(league.id)
  // Add/drop is gated on the draft being fully done (mirrors the
  // server-side addDropPlayer guard). Trust settings.draft_status
  // directly — league.status can drift (e.g. after draft starts but
  // before it completes, status is 'active' but rosters are still
  // draft-only). Salary cap leagues skip the draft entirely.
  const draftDone = settings?.format === 'salary_cap' || settings?.draft_status === 'completed'
  const isDraftPhase = !draftDone
  const isFaab = settings?.waiver_type === 'faab'
  const isWaiver = settings?.waiver_type === 'priority' || settings?.waiver_type === 'rolling' || isFaab
  const myWaiverState = waiverData?.me
  const reorderClaims = useReorderWaiverClaims(league.id)
  const [editingClaims, setEditingClaims] = useState(false)
  // Local override so an arrow tap moves the row immediately instead of
  // waiting on the round trip. null = show whatever the server sent, which is
  // already the ranked order.
  const [claimOrder, setClaimOrder] = useState(null)

  const serverPending = (myClaims || []).filter((c) => c.status === 'pending')
  const pendingClaims = useMemo(() => {
    if (!claimOrder) return serverPending
    const byId = new Map(serverPending.map((c) => [c.id, c]))
    // Anything the local order doesn't know about — a claim submitted in
    // another tab, or one cancelled since — is appended rather than dropped,
    // so the list can never hide a live claim.
    const seen = new Set(claimOrder)
    return [
      ...claimOrder.map((id) => byId.get(id)).filter(Boolean),
      ...serverPending.filter((c) => !seen.has(c.id)),
    ]
  }, [serverPending, claimOrder])

  async function moveClaim(index, delta) {
    const target = index + delta
    if (target < 0 || target >= pendingClaims.length) return
    const next = [...pendingClaims]
    ;[next[index], next[target]] = [next[target], next[index]]
    const ids = next.map((c) => c.id)
    setClaimOrder(ids)
    try {
      await reorderClaims.mutateAsync(ids)
    } catch (err) {
      // Snap back rather than leave the user looking at an order the
      // resolver won't actually use.
      setClaimOrder(null)
      toast(err.message || 'Failed to reorder claims', 'error')
    }
  }
  const claimedPlayerIds = new Set(pendingClaims.map((c) => c.add_player_id))

  // Roster ordered the same way the My Team view orders it: starters in
  // lineup-slot order (QB → RB1, RB2 → WR1, WR2 → TE → FLEX → SFLX → K →
  // DEF), then bench, then IR. Drives the "Pick a player to drop" list
  // below so it matches the user's mental layout of their roster.
  const sortedRoster = useMemo(() => {
    if (!roster?.length) return []
    const slots = settings?.roster_slots || { qb: 1, rb: 2, wr: 2, te: 1, flex: 1, k: 1, def: 1 }
    // Shared definition — lib/rosterSlots.
    const starterKeys = buildSlots(slots).map((sl) => sl.key)
    const orderIndex = (slot) => {
      const s = (slot || '').toLowerCase()
      const idx = starterKeys.indexOf(s)
      if (idx !== -1) return idx
      if (s === 'bench' || s.startsWith('bench')) return 1000
      if (s === 'ir' || s.startsWith('ir')) return 2000
      return 999
    }
    return [...roster].sort((a, b) => orderIndex(a.slot) - orderIndex(b.slot))
  }, [roster, settings?.roster_slots])

  // Total roster capacity from settings (starters + bench, IR slots are excluded
  // because IR'd players don't count toward the active roster)
  const rosterCap = (() => {
    const slots = settings?.roster_slots
    if (!slots) return 16
    let n = 0
    for (const [k, v] of Object.entries(slots)) {
      if (k === 'ir') continue
      n += Number(v) || 0
    }
    return n || 16
  })()
  // Active (non-IR) rostered count
  const activeRosterCount = (roster || []).filter((r) => r.slot !== 'ir').length

  async function handleConfirmAdd() {
    if (!addingPlayer) return
    if (activeRosterCount >= rosterCap && !dropPlayerId) {
      toast('Pick a player to drop', 'error')
      return
    }
    // Per-player waiver lock takes priority over the league-level waiver setting:
    // if a player is on waivers, the user must submit a claim, regardless of
    // whether the league uses waivers or instant adds.
    const playerOnWaivers = !!addingPlayer.on_waivers
    try {
      if (playerOnWaivers) {
        await submitClaim.mutateAsync({
          add_player_id: addingPlayer.id,
          drop_player_id: dropPlayerId || null,
          bid_amount: isFaab ? Number(bidAmount) || 0 : 0,
        })
        toast(`Waiver claim submitted for ${addingPlayer.full_name}`, 'success')
      } else {
        await addDrop.mutateAsync({
          addPlayerId: addingPlayer.id,
          dropPlayerId: dropPlayerId || null,
        })
        toast(`${addingPlayer.full_name} added`, 'success')
      }
      setAddingPlayer(null)
      setDropPlayerId('')
      setBidAmount(0)
    } catch (err) {
      toast(err.message || 'Failed to submit claim', 'error')
    }
  }

  // Pre-draft: show ADP / My Ranks toggle
  const showRankingsToggle = settings?.draft_status !== 'completed'

  return (
    <div className="space-y-3">
      {/* ADP / My Ranks toggle */}
      {showRankingsToggle && (
        <div className="flex gap-1 bg-bg-primary/40 rounded-lg p-1 border border-text-primary/10">
          {['ADP', 'My Ranks'].map((v) => (
            <button
              key={v}
              onClick={() => setPlayerView(v)}
              className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                playerView === v ? 'bg-accent text-white' : 'text-text-secondary hover:text-text-primary'
              }`}
            >{v}</button>
          ))}
        </div>
      )}

      {/* My Ranks view */}
      {playerView === 'My Ranks' && showRankingsToggle ? (
        <FantasyMyRankings league={league} />
      ) : <>

      {/* Waiver state summary */}
      {isWaiver && !isDraftPhase && myWaiverState && (
        <div className="rounded-xl border border-text-primary/20 px-4 py-3 bg-bg-primary/40 backdrop-blur-sm flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs">
            <div>
              <div className="text-[10px] uppercase text-text-muted">Priority</div>
              <div className="text-sm font-bold text-text-primary">#{myWaiverState.priority}</div>
            </div>
            {isFaab && (
              <div>
                <div className="text-[10px] uppercase text-text-muted">FAAB</div>
                <div className="text-sm font-bold text-accent">${myWaiverState.faab_remaining}</div>
              </div>
            )}
          </div>
          <span className="text-[10px] text-text-muted text-right">Waivers process Wed 3 AM ET</span>
        </div>
      )}

      {/* Pending claims */}
      {pendingClaims.length > 0 && (
        <div className="rounded-xl border border-text-primary/20 overflow-hidden bg-bg-primary/40">
          <div className="px-4 py-2 border-b border-text-primary/10 flex items-center justify-between gap-3">
            <h4 className="text-xs text-text-muted uppercase tracking-wider">Your Pending Claims</h4>
            {pendingClaims.length > 0 && (
              <button
                onClick={() => setEditingClaims((v) => !v)}
                className="text-[10px] font-semibold text-accent hover:text-accent/80 transition-colors uppercase tracking-wider"
              >
                {editingClaims ? 'Done' : 'Edit'}
              </button>
            )}
          </div>
          {/* Order is the whole point of this list, not decoration: the
              resolver works through a manager's claims top-down, so #1 is the
              one he actually ends up with when more than one could land. */}
          {editingClaims && pendingClaims.length > 1 && (
            <p className="px-4 py-1.5 text-[10px] text-text-muted border-b border-text-primary/10">
              Top claim is tried first. If it fails, the next one still gets a shot.
            </p>
          )}
          <div className="divide-y divide-border">
            {pendingClaims.map((claim, i) => (
              <div key={claim.id} className="flex items-center gap-3 px-4 py-2">
                {editingClaims && (
                  <span className="text-[10px] font-bold text-text-muted tabular-nums w-3 shrink-0">{i + 1}</span>
                )}
                <PlayerHeadshot
                  name={claim.add_player?.full_name}
                  url={claim.add_player?.headshot_url}
                  size="xs"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">+ {claim.add_player?.full_name}</div>
                  <div className="text-[10px] text-text-muted">
                    {claim.drop_player ? `Drop ${claim.drop_player.full_name}` : 'No drop'}
                    {isFaab && ` · $${claim.bid_amount}`}
                  </div>
                </div>
                {editingClaims && (
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      onClick={() => moveClaim(i, -1)}
                      disabled={i === 0 || reorderClaims.isPending}
                      aria-label="Move claim up"
                      className="px-1.5 py-0.5 rounded text-text-primary disabled:opacity-25 hover:bg-text-primary/10 transition-colors"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => moveClaim(i, 1)}
                      disabled={i === pendingClaims.length - 1 || reorderClaims.isPending}
                      aria-label="Move claim down"
                      className="px-1.5 py-0.5 rounded text-text-primary disabled:opacity-25 hover:bg-text-primary/10 transition-colors"
                    >
                      ▼
                    </button>
                  </div>
                )}
                {editingClaims && (
                  <button
                    onClick={async () => {
                      try {
                        await cancelClaim.mutateAsync(claim.id)
                        toast('Claim cancelled', 'success')
                      } catch (err) {
                        toast(err.message || 'Failed to cancel', 'error')
                      }
                    }}
                    className="text-[10px] font-semibold text-incorrect hover:text-incorrect/80 transition-colors shrink-0"
                  >
                    Cancel
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

    <div className="rounded-xl border border-text-primary/20 overflow-hidden bg-bg-primary">
      <div className="p-3 border-b border-text-primary/10">
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search players..."
          className="w-full bg-text-primary/5 border border-text-primary/15 rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <div className="flex gap-1 mt-2 overflow-x-auto scrollbar-hide">
          {buildPositionFilters(settings?.roster_slots).map((pos) => (
            <button
              key={pos}
              onClick={() => setPosFilter(pos)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                posFilter === pos ? 'bg-accent text-white' : 'bg-bg-secondary text-text-secondary hover:bg-border'
              }`}
            >
              {pos}
            </button>
          ))}
        </div>
      </div>
      {/* Season label for pre-draft (showing last year's stats) */}
      {settings?.draft_status === 'pending' && (
        <div className="px-4 py-1.5 text-[10px] text-text-muted font-semibold uppercase tracking-wider">
          2025 Stats
        </div>
      )}
      {/* Single scroll container — header sticks at top, everything scrolls horizontally together */}
      <div className="overflow-x-auto scrollbar-hide max-h-[65vh] overflow-y-auto overscroll-x-none">
        <div className="min-w-[900px]">
        {/* Header — sticky top so it stays visible during vertical scroll */}
        <div className="border-b border-text-primary/10 bg-bg-primary flex text-[10px] font-bold text-text-muted uppercase tracking-wider sticky top-0 z-30">
          <div className="sticky left-0 z-20 bg-bg-primary flex items-center shrink-0">
            <button
              type="button"
              onClick={() => handleSort('rank')}
              className={`w-8 text-center px-1 py-1.5 rounded transition-colors ${
                sortKey === 'rank' ? 'border border-accent text-accent' : 'hover:bg-bg-card'
              }`}
            >
              {sortKey === 'rank' ? '#↓' : '#'}
            </button>
            <div className="w-[135px] lg:w-[200px] px-1 py-1.5">Player</div>
            <div className="w-8" />
          </div>
          <div className="flex gap-0.5 py-1.5">
            {statColumns.map((col) => (
              <button
                key={col.key}
                type="button"
                onClick={() => handleSort(col.key)}
                className={`w-11 shrink-0 text-center px-0.5 py-0.5 rounded transition-colors ${
                  sortKey === col.key ? 'border border-accent text-accent' : 'hover:bg-bg-card'
                }`}
              >
                {col.label}{sortKey === col.key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
              </button>
            ))}
          </div>
        </div>
        {/* Rows */}
        <div>
          {isLoading ? (
            <div className="text-center text-sm text-text-muted py-8">Loading...</div>
          ) : (players || []).map((player, idx) => {
            const isClaimed = claimedPlayerIds.has(player.id)
            const onWaivers = !!player.on_waivers
            const pStats = player.stats || {}
            return (
              <div key={player.id} className="flex border-b border-border last:border-0">
                <div className="sticky left-0 z-10 bg-bg-primary flex items-center shrink-0">
                  <div className="w-8 text-center text-xs font-bold text-text-muted">
                    {player.adp_rank || idx + 1}
                  </div>
                  <div className="w-[135px] lg:w-[200px] flex items-center gap-2 px-1 py-2.5">
                    <div className="hidden md:block">
                      <PlayerHeadshot
                        name={player.full_name}
                        url={player.headshot_url}
                        size="md"
                        onClick={() => openPlayerDetail(player.id)}
                      />
                    </div>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openPlayerDetail(player.id)}>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-text-primary truncate hover:text-accent transition-colors">{player.full_name}</span>
                        <InjuryBadge status={player.injury_status} />
                        <BlurbDot playerId={player.id} blurbIds={blurbIds} />
                        {/* No "W" or "C" letters here. Waivers is already the
                            blue + (green + is a free agent), and a pending
                            claim already shows twice: the + swaps to a … in
                            the action column, and the row appears under Your
                            Pending Claims with a cancel button.
                            W was the least accurate thing in the row besides:
                            on_waivers means pool-membership OR kickoff-locked,
                            so it read "On waivers" over players who were never
                            dropped and whose team had simply already played. */}
                      </div>
                      <div className="text-[10px] text-text-muted">
                        {player.position} · {player.team || 'FA'}
                        {player.current_week_opponent ? (
                          <span className="ml-1.5">{player.current_week_is_home ? 'vs' : '@'} {player.current_week_opponent}</span>
                        ) : ('current_week_opponent' in player && player.team) ? (
                          <span className="ml-1.5 text-yellow-400 font-semibold">BYE</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="w-8 flex items-center justify-center">
                    {!isDraftPhase && roster?.length > 0 && !isClaimed && (
                      <button
                        onClick={() => setAddingPlayer(player)}
                        className={`text-lg font-bold w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                          onWaivers
                            ? 'text-blue-400 hover:bg-blue-400/15'
                            : 'text-correct hover:bg-correct/15'
                        }`}
                      >
                        +
                      </button>
                    )}
                    {!isDraftPhase && isClaimed && (
                      <span className="text-[9px] font-semibold text-text-muted">...</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-0.5 items-center">
                  {statColumns.map((col) => {
                    // Snap share must fall back to a DASH, not to 0 like the
                    // counting stats. A missing snap count means Sleeper did
                    // not report it — weeks before we started capturing them
                    // have none — and "0%" would say the player never took
                    // the field, which is the opposite claim and the exact
                    // thing someone checks this column to learn.
                    const value = col.key === 'weekly_proj'
                      ? (player.weekly_projection != null ? player.weekly_projection.toFixed(1) : '—')
                      : col.key === 'snap_pct'
                        ? (pStats.snap_pct != null ? `${pStats.snap_pct}%` : '—')
                        : (pStats[col.key] ?? 0)
                    return (
                      <div
                        key={col.key}
                        className={`w-11 shrink-0 text-center text-xs tabular-nums py-1 ${
                          sortKey === col.key ? 'text-text-primary font-bold' : 'text-text-secondary'
                        }`}
                      >
                        {value}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
          {!isLoading && players?.length === 0 && (
            <div className="text-center text-sm text-text-muted py-8">No players found</div>
          )}
        </div>
      </div>
    </div>
    </div>

      {/* Add/drop confirm modal */}
      {addingPlayer && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center" onClick={() => { setAddingPlayer(null); setBidAmount(0) }}>
          <div className="bg-bg-secondary w-full md:max-w-md rounded-2xl mx-3 mb-[calc(5.5rem+env(safe-area-inset-bottom))] md:mb-0 md:mx-0 p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg mb-4">{addingPlayer?.on_waivers ? 'Claim' : 'Add'} {addingPlayer.full_name}</h3>
            {isFaab && (
              <div className="mb-4">
                <label className="block text-xs uppercase text-text-muted mb-1.5">FAAB Bid (you have ${myWaiverState?.faab_remaining ?? 0})</label>
                <input
                  type="number"
                  min={0}
                  max={myWaiverState?.faab_remaining ?? 100}
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  className="w-full bg-bg-card border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent"
                />
              </div>
            )}
            {activeRosterCount >= rosterCap && (
              <>
                <p className="text-sm text-text-secondary mb-3">Roster is full. Pick a player to drop:</p>
                <div className="space-y-1 mb-4 max-h-60 overflow-y-auto">
                  {/* IR players are NOT droppable here. The roster-full test
                      above already excludes them (activeRosterCount), so
                      dropping one frees nothing — offering it let a manager
                      satisfy "roster is full" by cutting an IR player and end
                      up with 8 bench spots used out of 7 and an empty IR
                      slot. Dropping an IR player is still possible from the
                      roster view; it just isn't an answer to this prompt. */}
                  {sortedRoster.filter((r) => (r.slot || '').toLowerCase() !== 'ir').map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setDropPlayerId(r.player_id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-colors ${
                        dropPlayerId === r.player_id ? 'border-accent bg-accent/10' : 'border-border bg-bg-primary hover:bg-bg-card-hover'
                      }`}
                    >
                      <PlayerHeadshot
                        name={r.nfl_players?.full_name}
                        url={r.nfl_players?.headshot_url}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{r.nfl_players?.full_name}</div>
                        <div className="text-[10px] text-text-muted">{r.nfl_players?.position} · {r.slot.toUpperCase()}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="flex gap-2">
              <button onClick={() => { setAddingPlayer(null); setDropPlayerId(''); setBidAmount(0) }} className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-bg-card text-text-secondary border border-border hover:bg-bg-card-hover transition-colors">Cancel</button>
              <button
                onClick={handleConfirmAdd}
                disabled={addDrop.isPending || submitClaim.isPending}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
              >
                {(addDrop.isPending || submitClaim.isPending) ? 'Submitting…' : addingPlayer?.on_waivers ? 'Submit Claim' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailPlayerId && (() => {
        const detailPlayer = players?.find((p) => p.id === detailPlayerId)
        const ctx = detailPlayer?.on_waivers ? 'waiver' : 'free_agent'
        // Only expose Add / Claim once the draft is complete — mirrors
        // the row plus-sign gating and the server addDropPlayer guard.
        const canAddDrop = !isDraftPhase
        return (
          <PlayerDetailModal
            leagueId={league.id}
            playerId={detailPlayerId}
            onClose={() => setDetailPlayerId(null)}
            playerContext={ctx}
            onClaim={canAddDrop ? (pid) => {
              const p = players?.find((pl) => pl.id === pid)
              if (p) { setDetailPlayerId(null); setAddingPlayer(p) }
            } : undefined}
            onAdd={canAddDrop ? (pid) => {
              const p = players?.find((pl) => pl.id === pid)
              if (p) { setDetailPlayerId(null); setAddingPlayer(p) }
            } : undefined}
          />
        )
      })()}
    </>}
    </div>
  )
}
